<script>
    import { onMount } from "svelte";

    import { isMobile } from "../../stores";

    import { fly } from "svelte/transition";

    import {
        SearchInput,
        SvelteTable,
        Pagination,
        PageLoading,
        SearchSelect,
        LoadingModal,
        Button,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getAmountInCurrency,
        getFormatDateAndTime,
        getFormatDate,
        getText,
        goToURL,
        replaceURL,
        getNumberWithComma,
        isObjectEmpty,
        showError,
        showException,
        goToDownloadPage,
        getInvoiceStatus,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
    } from "../../Utils/utilFunctions.svelte";

    import { invoiceStatus, pageOptions } from "../../Utils/utilEnums.svelte";

    import { user } from "../../stores";

    import CreateAccountInvoiceModal from "./CreateAccountInvoiceModal.svelte";
    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

    export let params = {};

    let isGettingAccountInvoices = false,
        returnAccountInvoicesArr = [],
        filters = { status: "1,2" },
        page = 0,
        pageSize = 25,
        count,
        checkboxString = "",
        selectedAccountInvoices = [],
        selectedAccountInvoicesIdArr = [],
        isDownloadingAccountInvoice = false,
        isRegeneratingAccountInvoice = false,
        isSendingInvoiceToDoctor = false,
        isInvoiceSendToDoctor = false,
        isCreateInvoiceModalOpen = false,
        showFilters = false;

    let selectedDoctors = [];

    const columns = [
        {
            key: "selected?",
            title: "",
            headerClass: "toggleSelectAllHeader text-center ml-3 rounded-tl-xl",
            renderValue: (v) =>
                `<input type="checkbox" class="checkbox checkbox-primary checkbox-xs ml-3 text-white cursor-pointer" name="selectCheckBox" id="selectCheckBoxId${v.id}">`,
            csvExportHidden: true,
        },
        {
            key: "id",
            title: "Invoice ID",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "invoiceNumber",
            title: "Invoice Number",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.invoiceNumber),
        },
        {
            key: "doctor",
            title: "Doctor",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getAccountName(v.billingDetails),
        },
        {
            key: "createdDate",
            title: "Fortnight ending",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getFormatDate(v.createdDate, false, true),
        },
        {
            key: "dueDate",
            title: "Due Date",
            headerClass: "text-left px-3",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getFormatDate(v.dueDate, false, true),
        },
        {
            key: "subTotal",
            title: "Sub-Total",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.totalFee),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.totalFee / 10),
        },
        // {
        //     key: "fee",
        //     title: "Fee",
        //     headerClass: "text-left px-3 cursor-default",
        //     class: "text-left px-3 cursor-pointer",
        //     value: (v) => getAmountInCurrency(v.totalFee),
        // },
        {
            key: "totalFee",
            title: "Total",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) =>
                getAmountInCurrency(v.totalFee * (v.addGST ? 1.1 : 1)),
        },
        {
            key: "status",
            title: "Status",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getStatus(v.status),
            csvContent: (v) => _getStatus(v.status, true),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(),
            csvExportHidden: true,
        },
    ];

    const sortableHeaderKeys = ["id", "invoiceNumber", "createdDate"];

    $: isUserEnableViewAccountInvoice = $user.role >= 20;

    $: _getCheckboxString(selectedAccountInvoicesIdArr);
    $: if (
        selectedAccountInvoices.length >= 0 &&
        returnAccountInvoicesArr.length > 0
    ) {
        selectedAccountInvoicesIdArr = selectedAccountInvoices.map(
            (invoice) => invoice.id,
        );
        _updateHeaderIcon();
    }

    onMount(async () => {
        if (params.query) updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        loadAccountInvoices(page * pageSize, pageSize, _getSearchParams());

        if (params.query) updateCheckboxByQuery(params.query);
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];

                if (arr[i].includes("search="))
                    filters.invoiceNumber = arr[i].split("=")[1].toString();

                if (arr[i].includes("status="))
                    filters.status = isNaN(arr[i].split("=")[1])
                        ? arr[i].split("=")[1]
                        : Number(arr[i].split("=")[1]);

                if (arr[i].includes("doctorId=")) {
                    if (isNaN(arr[i].split("=")[1])) {
                        filters.doctorId = arr[i].split("=")[1];
                        selectedDoctors = filters.doctorId
                            .split(",")
                            .map((id) => Number(id));
                    } else {
                        filters.doctorId = Number(arr[i].split("=")[1]);
                        selectedDoctors = [Number(filters.doctorId)];
                    }
                }

                if (arr[i].includes("createddatefrom="))
                    filters.createddatefrom = arr[i].split("=")[1];

                if (arr[i].includes("createddateto="))
                    filters.createddateto = arr[i].split("=")[1];
            }
            updateURL();
        }
    };

    const updateCheckboxByQuery = (query) => {
        const arr = query.split("&");
        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("check=")) {
                    const checkArr = arr[i].split("=")[1].split(",");
                    checkArr.map((ca) => {
                        if (
                            !selectedAccountInvoices.some(
                                (p) => p.id == Number(ca),
                            )
                        ) {
                            let newInvoiceItem = { id: Number(ca) };
                            selectedAccountInvoices = [
                                ...selectedAccountInvoices,
                                newInvoiceItem,
                            ];
                            const checkbox = document.getElementById(
                                `selectCheckBoxId${ca}`,
                            );
                            if (checkbox) checkbox.checked = true;
                        }
                    });
                }
            }
        }
    };

    const loadAccountInvoices = async (start, limit, params) => {
        isGettingAccountInvoices = true;
        if (params.sort) params.sort = params.sort.toLowerCase();

        await apiMethods.getMethod
            .getAccountInvoice(start, limit, params)
            .then((res) => _handleAccountInvoices(res))
            .then(() => _updateHeaderSortIcon());

        isGettingAccountInvoices = false;
    };

    const handleFilterSelect = () => {
        page = 0;
        selectedAccountInvoices = [];

        updateURL();

        loadAccountInvoices(0, pageSize, _getSearchParams());
    };

    const generateSelectAll = () => {
        const selectAllHeader = document.getElementsByClassName(
            "toggleSelectAllHeader",
        )[0];
        const tag = document.createElement("input");
        tag.type = "checkbox";
        tag.className =
            "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white ml-3 mt-2";
        tag.id = "toggleSelectAll";
        tag.onclick = () => {
            toggleSelectAll();
        };
        if (selectAllHeader) selectAllHeader.appendChild(tag);
    };

    const toggleSelectAll = () => {
        const selectAllCheckBox = document.getElementById("toggleSelectAll");
        if (selectAllCheckBox.className.includes("selectedAll")) {
            selectedAccountInvoices = [];
            _updateCheckBox();
        } else {
            returnAccountInvoicesArr.forEach((invoice) => {
                if (!selectedAccountInvoicesIdArr.includes(invoice.id)) {
                    _addSelectedInvoice(invoice);
                }
            });
        }
    };

    const updateURL = () => {
        if (checkboxString)
            replaceURL(
                `/AccountInvoice/page=${page}&size=${pageSize}&check=${checkboxString}${_getParamsString()}`,
            );
        else
            replaceURL(
                `/AccountInvoice/page=${page}&size=${pageSize}${_getParamsString()}`,
            );
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadAccountInvoices(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );

        _updateCheckBox();
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadAccountInvoices(0, newPageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "selected?") _addSelectedInvoice(rowData);
        else if (key == "actions") {
            if (className.includes("downloadIcon"))
                downloadAccountInvoice(rowData.id);
            else if (className.includes("reGenerateIcon"))
                regenerateAccountInvoice(rowData);
            else if (className.includes("sendToDoctorIcon"))
                sendInvoice(rowData.id);
        } else if (rowData.id && isUserEnableViewAccountInvoice)
            goToURL(`/AccountInvoice/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (key == "selected?") _updateCheckBox();
        else if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadAccountInvoices(0, pageSize, _getSearchParams());
        }
    };

    const downloadAccountInvoice = (id, isRegenerate) => {
        goToDownloadPage(5, id, isRegenerate);
    };

    const regenerateAccountInvoice = async (data) => {
        const regenerateObj = {
            type: 5,
            entityId: data.id,
            doctorId: data.doctorId,
            patientId: 0,
            patientEncounterId: 0,
        };

        isRegeneratingAccountInvoice = true;

        await apiMethods.postMethod
            .regeneratePdfFile(regenerateObj)
            .then((res) => {
                if (res.id) downloadAccountInvoice(data.id, true);
                else
                    showError(
                        "Failed to re-generate this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) =>
                showException(
                    "Failed to re-generate this invoice PDF, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isRegeneratingAccountInvoice = false;
    };

    const sendInvoice = async (id) => {
        isSendingInvoiceToDoctor = true;

        await apiMethods.getMethod
            .getAccountInvoiceById(id)
            .then(async (res) => {
                if (!res || !res.id || res.error)
                    showError(
                        "Failed to load invoice details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else
                    await apiMethods.postMethod
                        .sendInvoiceToDoctor(res)
                        .then((res) => {
                            if (res.id) isInvoiceSendToDoctor = true;
                            else
                                showError(
                                    "Failed to send invoice to doctor, please refresh the page and try again and contact support if the issue persists.",
                                );
                        });
            })
            .catch((err) => showException(err));

        isSendingInvoiceToDoctor = false;
    };

    const showCreateInvoiceModal = () => {
        isCreateInvoiceModalOpen = true;
    };

    const closeCreateInvoiceModal = () => {
        isCreateInvoiceModalOpen = false;
    };

    const handleCreateInvoice = (res) => {
        if (res.id)
            loadAccountInvoices(page * pageSize, pageSize, _getSearchParams());
        else if (res.error) showError(res.error);
    };

    // HELPER FUNCTIONS
    const _handleAccountInvoices = (res) => {
        if (res.error) showError(res.error);
        else {
            returnAccountInvoicesArr = [...res.results];
            count = res.total;
        }

        setTimeout(() => {
            generateSelectAll();
            _updateCheckBox();
        }, 1);
    };

    const _updateCheckBox = () => {
        setTimeout(() => {
            returnAccountInvoicesArr.forEach((invoice) => {
                const checkbox = document.getElementById(
                    `selectCheckBoxId${invoice.id}`,
                );
                if (checkbox) {
                    if (selectedAccountInvoicesIdArr.includes(invoice.id)) {
                        document.getElementById(
                            `selectCheckBoxId${invoice.id}`,
                        ).checked = true;
                    } else {
                        document.getElementById(
                            `selectCheckBoxId${invoice.id}`,
                        ).checked = false;
                    }
                }
            });
            _updateHeaderIcon();
        }, 1);
    };

    const _addSelectedInvoice = (data) => {
        if (selectedAccountInvoices.some((invoice) => invoice.id === data.id)) {
            selectedAccountInvoices = selectedAccountInvoices.filter(
                (invoice) => invoice.id !== data.id,
            );
        } else {
            selectedAccountInvoices = [...selectedAccountInvoices, data];
            const checkbox = document.getElementById(
                `selectCheckBoxId${data.id}`,
            );
            if (checkbox) checkbox.checked = true;
        }
    };

    const _getCheckboxString = (arr) => {
        if (arr.length) checkboxString = arr.toString();
        else checkboxString = "";

        updateURL();
    };

    const _updateHeaderIcon = () => {
        const headerIcon = document.getElementById("toggleSelectAll");
        if (!headerIcon) return;
        if (selectedAccountInvoices.length === 0) {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedNone";
            headerIcon.checked = false;
            headerIcon.style = "";
        } else if (
            returnAccountInvoicesArr.every((invoice) =>
                selectedAccountInvoicesIdArr.includes(invoice.id),
            )
        ) {
            headerIcon.style.cssText = "";
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedAll";
            headerIcon.checked = true;
        } else {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 appearance-none selectedPartial";
            headerIcon.checked = false;
            headerIcon.style.cssText =
                "border: solid #270053; border-width: 4px; padding: 4px; border-radius: 5px; background-color: white; cursor: pointer; margin: 0.5rem 0 0 0.75rem";
        }
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "id" &&
                            row.childNodes[0].data == "Invoice ID") ||
                        (params.sort == "invoiceNumber" &&
                            row.childNodes[0].data == "Invoice Number  ") ||
                        (params.sort == "createdDate" &&
                            row.childNodes[0].data == "Fortnight ending")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "idDesc" &&
                            row.childNodes[0].data == "Invoice ID") ||
                        (params.sort == "invoiceNumberDesc" &&
                            row.childNodes[0].data == "Invoice Number  ") ||
                        (params.sort == "createdDateDesc" &&
                            row.childNodes[0].data == "Fortnight ending")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.sort) params.sort = filters.sort;

        if (filters.invoiceNumber) params.search = filters.invoiceNumber;

        if (filters.status) params["filter[status]"] = filters.status;

        if (filters.createddatefrom)
            params["filter[createddatefrom]"] = filters.createddatefrom;

        if (filters.createddateto)
            params["filter[createddateto]"] = filters.createddateto;

        if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;

            if (params.search) str += `&search=${params.search}`;

            const filterKeys = [
                "status",
                "createddatefrom",
                "createddateto",
                "doctorId",
            ];

            filterKeys.forEach((key) => {
                if (params[`filter[${key}]`])
                    str += `&${key}=` + params[`filter[${key}]`];
            });
        }

        return encodeSearchParams(str);
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        if (filters.doctorId) {
            delete filters.doctorId;

            handleFilterSelect();
        }
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="reGenerateIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Re-generate Invoice PDF">
                        <i class="reGenerateIcon fas fa-arrows-rotate"></i>
                    </div>

                    <div class="downloadIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Download Invoice PDF">
                        <i class="downloadIcon fas fa-download"></i>
                    </div>

                    <div class="sendToDoctorIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Send Invoice to Doctor">
                        <i class="sendToDoctorIcon fa-solid fa-paper-plane"></i>
                    </div>
                </div>`;
    };

    const _getAccountName = (billingDetails) => {
        if (isObjectEmpty(billingDetails)) return "";

        return getText(billingDetails.name);
    };

    const _getFormatDate = (date, isExpired, isDateOnly) => {
        if (isExpired) return "Expired";

        if (isDateOnly) return getFormatDate(date);

        return getFormatDateAndTime(date);
    };
    const _getStatus = (status, isCsvContent = false) => {
        let statusText, statusColor;

        statusText = getInvoiceStatus(status);

        if (isCsvContent) return statusText;

        switch (status) {
            case 1:
            case 2:
                statusColor = "#2DBD6F";
                break;
            case 9:
                statusColor = "#E05757";
                break;
            default:
                statusColor = "";
        }

        return `
                <div class="flex justify-start items-center">
                    <div style="color: ${statusColor};">${statusText}</div>
                </div>
                `;
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => fly(node, args);
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-32 flex flex-row gap-x-1"
            on:click={toggleFilters}
        >
            Filters
            <i class="fa fa-plus mr-2 text-[14px]" aria-hidden="true" />
            <i
                style="font-size:14px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
        {#if showFilters}
            <div
                class="flex flex-0 items-start flex-col-reverse gap-y-4"
                in:filterTransition={{ duration: 200, y: -50 }}
            >
                <div class="flex gap-4 flex-wrap items-center">
                    <SearchInput
                        bind:value={filters.invoiceNumber}
                        title="Search or Invoice ID"
                        inputFunc={handleFilterSelect}
                    />
                    <SearchSelect
                        bind:value={filters.status}
                        title="Status"
                        onChangeFunc={handleFilterSelect}
                    >
                        <option value="1,2" selected>
                            All except cancelled
                        </option>
                        {#each invoiceStatus as { id, type }}
                            <option value={id} selected={id == filters.status}>
                                {type}
                            </option>
                        {/each}
                    </SearchSelect>

                    <DefaultDoctorFilter
                        bind:filter={filters}
                        filterPropertyName={"doctorId"}
                        onChangeFunc={() => {
                            selectedDoctors = filters["doctorId"].split(",");
                            handleFilterSelect();
                        }}
                    />

                    <DoctorMultiSearchSelect
                        bind:searchResult={selectedDoctors}
                        onChangeFunc={handleSelectDoctor}
                        inputClass="w-32"
                        zIndex="3"
                    />

                    {#if !$user.settings?.disableDateRangeInput}
                        <SearchDateRangeInput
                            bind:startDAte={filters.createddatefrom}
                            bind:endDate={filters.createddateto}
                            title="Fortnight Date Range"
                            inputClass="borderB"
                            on:change={handleFilterSelect}
                            showClearButton
                            disabled={isGettingAccountInvoices}
                            showScrollYearOptions
                        />
                    {:else}
                        <SearchInput
                            bind:value={filters.createddatefrom}
                            title="Fortnight ending from"
                            type="date"
                            inputClass="borderB"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingAccountInvoices}
                            maxDate={filters.createddateto ?? undefined}
                            showScrollYearOptions
                        />

                        <SearchInput
                            bind:value={filters.createddateto}
                            title="Fortnight ending to"
                            type="date"
                            inputClass="borderB"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingAccountInvoices}
                            minDate={filters.createddatefrom ?? undefined}
                            showScrollYearOptions
                        />
                    {/if}

                    <ResetFilterLink
                        bind:filter={filters}
                        onResetFilter={() => {
                            handleFilterSelect();
                            clearFilterDoctor();
                        }}
                    />
                </div>

                <Button clickFunc={showCreateInvoiceModal}>
                    Create Invoice
                </Button>
            </div>
        {/if}
    {:else}
        <div class="flex flex-row w-full justify-between">
            <div
                class="flex w-auto gap-4 flex-wrap items-end justify-start"
                in:fly={{ duration: 200, y: -50 }}
            >
                <SearchInput
                    bind:value={filters.invoiceNumber}
                    title="Search or Invoice ID"
                    inputFunc={handleFilterSelect}
                />
                <SearchSelect
                    bind:value={filters.status}
                    title="Status"
                    onChangeFunc={handleFilterSelect}
                >
                    <option value="1,2" selected> All except cancelled </option>
                    {#each invoiceStatus as { id, type }}
                        <option value={id} selected={id == filters.status}>
                            {type}
                        </option>
                    {/each}
                </SearchSelect>

                <DefaultDoctorFilter
                    bind:filter={filters}
                    filterPropertyName={"doctorId"}
                    onChangeFunc={() => {
                        selectedDoctors = filters["doctorId"].split(",");
                        handleFilterSelect();
                    }}
                />

                <DoctorMultiSearchSelect
                    bind:searchResult={selectedDoctors}
                    onChangeFunc={handleSelectDoctor}
                    inputClass="w-32"
                    zIndex="3"
                />

                {#if !$user.settings?.disableDateRangeInput}
                    <SearchDateRangeInput
                        bind:startDAte={filters.createddatefrom}
                        bind:endDate={filters.createddateto}
                        title="Fortnight Date Range"
                        inputClass="borderB"
                        on:change={handleFilterSelect}
                        showClearButton
                        disabled={isGettingAccountInvoices}
                        showScrollYearOptions
                    />
                {:else}
                    <SearchInput
                        bind:value={filters.createddatefrom}
                        title="Fortnight ending from"
                        type="date"
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingAccountInvoices}
                        maxDate={filters.createddateto ?? undefined}
                        showScrollYearOptions
                    />

                    <SearchInput
                        bind:value={filters.createddateto}
                        title="Fortnight ending to"
                        type="date"
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingAccountInvoices}
                        minDate={filters.createddatefrom ?? undefined}
                        showScrollYearOptions
                    />
                {/if}

                <ResetFilterLink
                    bind:filter={filters}
                    onResetFilter={() => {
                        handleFilterSelect();
                        clearFilterDoctor();
                    }}
                />
            </div>

            <Button clickFunc={showCreateInvoiceModal}>Create Invoice</Button>
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingAccountInvoices}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={returnAccountInvoicesArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <!-- <div class="flex flex-col">
        <div class="flex flex-1 justify-between items-center">
            <div class="flex flex-row items-center gap-x-4">
                <div
                    class="flex items-center {$isMobile
                        ? 'flex-col'
                        : 'flex-row'}"
                >
                    <strong class="text-gray-600 text-sm">Table size:</strong>
                    <select
                        class="select select-sm text-gray-600 m-2"
                        bind:value={pageSize}
                        on:change|preventDefault={() =>
                            onPageSizeChange(pageSize)}
                    >
                        {#each pageOptions as pageOption}
                            <option
                                value={pageOption.amount}
                                selected={pageOption.amount === pageSize}
                            >
                                {pageOption.amount}
                            </option>
                        {/each}
                    </select>
                </div>

                <div class="flex flex-row items-center gap-x-6">
                    {#if $isMobile}
                        <strong class="text-gray-600 text-sm flex flex-col">
                            Total:
                            <span class="text-gray-600 h-4 my-4 align-middle"
                                >{getNumberWithComma(count)}</span
                            >
                        </strong>
                        <strong class="text-gray-600 text-sm flex flex-col">
                            Selected:
                            <span class="text-gray-600 h-4 my-4 align-middle"
                                >{getNumberWithComma(
                                    selectedAccountInvoices.length,
                                )}</span
                            >
                        </strong>
                    {:else}
                        <strong class="text-gray-600 text-sm">
                            Total records:
                            <span class="text-gray-600"
                                >{getNumberWithComma(count)}</span
                            >
                        </strong>
                        <strong class="text-gray-600 text-sm">
                            Selected records:
                            <span class="text-gray-600"
                                >{getNumberWithComma(
                                    selectedAccountInvoices.length,
                                )}</span
                            >
                        </strong>
                    {/if}
                </div>
            </div>

            {#if returnAccountInvoicesArr.length}
                <Pagination
                    {page}
                    {pageSize}
                    {count}
                    on:onPageChange={onPageChange}
                />
            {/if}
        </div>
    </div> -->

    <TableFooter
        {page}
        {pageSize}
        {count}
        showSelected
        selectedCount={selectedAccountInvoices.length}
        tableData={returnAccountInvoicesArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnAccountInvoicesArr,
            fileName: "Doctor Invoices",
            allDataApi: async () =>
                await apiMethods.getMethod.getAccountInvoice(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

{#if isCreateInvoiceModalOpen}
    <CreateAccountInvoiceModal
        {isCreateInvoiceModalOpen}
        {closeCreateInvoiceModal}
        {handleCreateInvoice}
    />
{/if}

{#if isDownloadingAccountInvoice}
    <LoadingModal titleText="Downloading Invoice PDF..." />
{/if}

{#if isRegeneratingAccountInvoice}
    <LoadingModal titleText="Regenerating Invoice PDF..." />
{/if}

{#if isSendingInvoiceToDoctor}
    <LoadingModal titleText="Sending Invoice to Doctor..." />
{/if}

<div
    class={isInvoiceSendToDoctor ? "modal modal-open sm:modal-middle" : "modal"}
>
    <div class="modal-box">
        <div class="p-4">
            <p class="leading-6 text-secondary whitespace-pre-line">
                Invoice send to doctor.
            </p>
        </div>
        <div class="modal-action">
            <button
                class="btn btn-secondary text-white btn-sm border-none w-24"
                on:click={() => (isInvoiceSendToDoctor = false)}
            >
                Done
            </button>
        </div>
    </div>
</div>
